import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FileText, History, Table2, Settings2, LayoutDashboard, PhoneCall, Users, Trophy, LogOut, KeyRound, ChevronDown, PenLine, UserCog, Menu, Wallet } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useSettings } from "@/lib/hooks";
import { api } from "@/lib/api";
import { counselorOf } from "@/lib/status";
import { useAuth } from "@/lib/auth";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChangePasswordForm } from "@/components/ChangePassword";
import { SignatureDialog } from "@/components/SignatureDialog";
import { ProfileDialog } from "@/components/ProfileDialog";

const NAV = [
  { to: "/", label: "Receipt Builder", icon: FileText, id: "builder" },
  { to: "/dashboard", label: "Summary", icon: LayoutDashboard, id: "dashboard" },
  { to: "/follow-ups", label: "Follow-ups", icon: PhoneCall, id: "follow-ups" },
  { to: "/history", label: "History", icon: History, id: "history" },
  { to: "/team", label: "Team", icon: Trophy, id: "team", admin: true },
  { to: "/fee-master", label: "Fee Master", icon: Table2, id: "fee-master", admin: true },
  { to: "/staff", label: "Staff", icon: Users, id: "staff", admin: true },
  { to: "/settings", label: "Settings", icon: Settings2, id: "settings", admin: true },
];

const useCallsDue = () => {
  const { user } = useAuth();
  const { data = [] } = useQuery({ queryKey: ["receipts"], queryFn: () => api.get("/receipts").then((r) => r.data), refetchInterval: 300000 });
  const today = new Date().toLocaleDateString("en-CA");
  const open = data.filter((r) => r.status !== "admitted" && r.status !== "not_interested" && r.follow_up_date && r.follow_up_date <= today);
  const me = (user.name || user.email).trim().replace(/\s+/g, " ").toLowerCase();
  const count = (list) => ({ n: list.length, overdue: list.filter((r) => r.follow_up_date < today).length });
  const mine = count(open.filter((r) => counselorOf(r).toLowerCase() === me));
  const team = count(open);
  return { mine: mine.n, mineOverdue: mine.overdue, team: team.n };
};

const Badge = ({ n, testid, amber }) => (n > 0 ? (
  <span className={`grid h-5 min-w-5 place-items-center rounded-full px-1.5 font-num text-[11px] font-bold ${amber ? "bg-amber-400 text-amber-950 ring-2 ring-amber-200" : "bg-red-600 text-white"}`}
    data-testid={testid} data-overdue={amber ? "true" : "false"}>{n}</span>
) : null);

const MobileMenu = ({ items, calls }) => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const current = items.find((n) => n.to === pathname);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm font-medium transition-colors duration-150 sm:hidden ${current ? "border-plum-900 bg-plum-900 text-white" : "border-plum-900/15 text-plum-950 hover:bg-plum-900/5"}`} data-testid="mobile-nav-trigger">
        <Menu className="h-4 w-4" />Menu<Badge n={calls.mine} testid="mobile-nav-calls-badge" amber={calls.mineOverdue > 0} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {items.map(({ to, label, icon: Icon, id }) => (
          <DropdownMenuItem key={to} onClick={() => nav(to)} className={pathname === to ? "bg-plum-900/5 font-semibold text-plum-950" : ""} data-testid={`mobile-nav-${id}`}>
            <Icon className="mr-2 h-4 w-4" />{label}
            {id === "follow-ups" && (calls.mine > 0 || calls.team > 0) && (
              <span className="ml-auto text-[11px] text-slate-500" data-testid="mobile-nav-calls-detail">
                <b className={calls.mineOverdue ? "text-amber-600" : "text-red-600"}>{calls.mine}</b> mine{calls.mineOverdue ? ` (${calls.mineOverdue} overdue)` : ""} · {calls.team} team
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserMenu = () => {
  const { user, logout, isAccountant } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [profOpen, setProfOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-150 hover:bg-plum-900/5" data-testid="user-menu-trigger">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-plum-900 text-xs font-semibold text-plum-300">{(user.name || user.email)[0].toUpperCase()}</span>
          <span className="hidden leading-tight md:block">
            <span className="block text-sm font-medium text-plum-950" data-testid="user-menu-name">{user.name || user.email}</span>
            <span className="block text-[11px] capitalize text-slate-500" data-testid="user-menu-role">{user.role}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs font-normal text-slate-500">{user.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setProfOpen(true)} data-testid="menu-profile"><UserCog className="mr-2 h-4 w-4" />My profile</DropdownMenuItem>
          {!isAccountant && <DropdownMenuItem onClick={() => setSigOpen(true)} data-testid="menu-signature"><PenLine className="mr-2 h-4 w-4" />My signature</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="menu-change-password"><KeyRound className="mr-2 h-4 w-4" />Change password</DropdownMenuItem>
          <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SignatureDialog open={sigOpen} onOpenChange={setSigOpen} />
      <ProfileDialog open={profOpen} onOpenChange={setProfOpen} />
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Change password</DialogTitle></DialogHeader>
          <ChangePasswordForm onDone={() => setPwOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const AppShell = ({ children }) => {
  const { data: s } = useSettings();
  const { isAdmin, isAccountant } = useAuth();
  const items = isAccountant ? [{ to: "/", label: "Payments", icon: Wallet, id: "payments" }] : NAV.filter((n) => !n.admin || isAdmin);
  const calls = useCallsDue();
  const menuItems = items.filter((n) => n.id !== "builder" && n.id !== "payments");
  return (
    <div className="min-h-screen">
      <header className="no-print sticky top-0 z-30 border-b border-plum-900/10 bg-white/85 backdrop-blur-xl">
        <div className="relative mx-auto flex max-w-[1500px] flex-wrap items-center gap-x-8 gap-y-3 px-4 py-3 sm:px-8">
          <div className="flex items-center gap-4" data-testid="app-brand">
            <img src="/logo.png" alt={s?.school_name || "School logo"} className="h-11 w-auto" data-testid="app-logo" />
            <div className="hidden border-l border-plum-900/15 pl-4 text-[11px] uppercase leading-tight tracking-[0.18em] text-slate-500 sm:block">
              Counselor Desk<br />{s?.academic_year}
            </div>
          </div>
          <nav className="flex w-full min-w-0 gap-1 overflow-x-auto sm:w-auto sm:flex-1">
            {items.map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                end
                data-testid={`nav-${id}`}
                className={({ isActive }) =>
                  `${id === "builder" || id === "payments" ? "flex" : "hidden sm:flex"} shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-plum-900 text-white" : "text-slate-600 hover:bg-plum-900/5 hover:text-plum-950"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
                {id === "follow-ups" && (
                  <span className="flex items-center gap-1" title={`${calls.mine} yours${calls.mineOverdue ? ` (${calls.mineOverdue} overdue)` : ""} · ${calls.team} team calls`}>
                    <Badge n={calls.mine} testid="nav-calls-badge" amber={calls.mineOverdue > 0} />
                    {calls.team > calls.mine && <span className="rounded-full bg-slate-200 px-1.5 font-num text-[11px] font-semibold text-slate-700" data-testid="nav-calls-team">{calls.team} team</span>}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="absolute right-4 top-3 flex items-center gap-2 sm:static">{menuItems.length > 0 && <MobileMenu items={menuItems} calls={calls} />}<UserMenu /></div>
        </div>
      </header>
      <main className="mx-auto max-w-[1500px] px-4 pb-8 pt-3 sm:px-8">{children}</main>
    </div>
  );
};
